// The editable cells of a board row: status, priority, people, dates.
//
// Each one saves on change and reports the error verbatim if the save is
// refused. A cell that silently reverts is worse than one that shows a red
// message, because the operator walks away believing the change landed.
import { useState, type ReactNode } from 'react'
import { AlertTriangle, Car, Pencil, UserRound } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '../../lib/shadcn/utils'
import {
  AUTHORITY_CLASS,
  AUTHORITY_HINT,
  AUTHORITY_OPTIONS,
  AUTHORITY_SHORT,
  GROUP_LABEL,
  dateInputValue,
  PRIORITY_CLASS,
  PRIORITY_OPTIONS,
  STATUS_CLASS,
  STATUS_OPTIONS,
  bubbleClass,
  groupForStatus,
  initials,
  shortDate,
  type BoardGroup,
  type TaskLink,
  type TaskAuthority,
  type TaskLinkOption,
  type TaskPerson,
  type TaskPriority,
  type TaskStatus,
  longDateTime,
} from '../../utils/tasks'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../lib/shadcn/tooltip'
import { MultiSelect, type Choice } from './TaskPickers'

export type SavePatch = (patch: Record<string, unknown>) => Promise<void>

// LOOKING EDITABLE
//
// Every cell on this board has saved on click since it shipped, and nobody
// used it, because a date rendered as small grey text looks like a report. The
// capability was never the problem; the invitation was missing.
//
// It has to stay quiet. This board carries 253 rows and the same argument that
// keeps the link cell from putting a dashed pill on all of them applies here:
// 253 invitations nobody asked for drown out the twenty five rows that need
// attention. So the pencil costs nothing until the pointer is on the cell, and
// it is positioned absolutely, so a hover never moves a single character of
// what is being read.
const EDIT_HINT =
  'pointer-events-none absolute right-0.5 top-1/2 -translate-y-1/2 opacity-0 ' +
  'transition-opacity group-hover:opacity-60'

function EditPencil() {
  return <Pencil className={cn(EDIT_HINT, 'h-3 w-3')} />
}

// THE SNAP-BACK, AND WHY IT WAS THE REAL PROBLEM
//
// Every cell here is a controlled input rendering the value on the row. Choose a
// new status and the browser paints your choice at once, React re-renders with
// the row's old value a tick later, and the pill goes back to what it was. Then
// the board reloads and it changes again. Nothing is broken and it reads as
// broken: three states for one click, and the middle one says the click failed.
//
// `pending` is what was chosen, held for exactly as long as the request is in
// flight. The cell shows it, so a save looks like one step and not three. It is
// cleared whether the write succeeded or failed: on success the row underneath
// already carries the confirmed value, and on failure the cell must go back to
// the truth with the error beside it.
//
// This is deliberately NOT an optimistic update. Nothing but this one control
// moves. The card stays in its group and the counts stay put until the database
// has confirmed. See hooks/confirmedWrites.ts for why that trade was made.
function useSaver(save: SavePatch) {
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState<Record<string, unknown> | null>(null)
  const run = async (patch: Record<string, unknown>) => {
    setBusy(true)
    setPending(patch)
    try {
      await save(patch)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The change was not saved.')
    } finally {
      setBusy(false)
      setPending(null)
    }
  }
  return { busy, run, pending }
}

/** The chosen value for one field while it saves, or the row's value. */
function shown<T>(pending: Record<string, unknown> | null, field: string, value: T): T {
  return pending && field in pending ? (pending[field] as T) : value
}

// A select that looks like a coloured pill, which is how the board this
// replaces showed status. The native element is deliberate: it is the only
// control that opens correctly on a phone without any extra work, and three of
// six people run this business from one.
export function StatusCell({
  value,
  group,
  save,
  disabled,
}: {
  value: TaskStatus
  // Only used to name the group the card is about to move to. The database
  // decides where it actually goes; this is a label, never an instruction.
  group?: BoardGroup
  save: SavePatch
  disabled?: boolean
}) {
  const { busy, run, pending } = useSaver(save)
  // What the operator chose, until the server has answered. See useSaver.
  const live = shown<TaskStatus>(pending, 'status', value)
  return (
    <div className="relative">
      <select
        value={live}
        disabled={disabled || busy}
        title={
          busy
            ? 'Saving. The card moves once the database confirms it.'
            : group
              ? `Changing the status moves this card. ${live} keeps it in ${GROUP_LABEL[groupForStatus(live, group)]}.`
              : undefined
        }
        onChange={(e) => void run({ status: e.target.value })}
        className={cn(
          'w-full cursor-pointer appearance-none rounded-md px-2 py-1 text-center text-xs font-semibold',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-60',
          busy && 'animate-pulse',
          STATUS_CLASS[live],
        )}
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s} className="bg-background text-foreground">
            {s}
          </option>
        ))}
      </select>
    </div>
  )
}

export function PriorityCell({
  value,
  save,
  disabled,
}: {
  value: TaskPriority | null
  save: SavePatch
  disabled?: boolean
}) {
  const { busy, run, pending } = useSaver(save)
  const live = shown<TaskPriority | null>(pending, 'priority', value)
  return (
    <select
      value={live ?? ''}
      disabled={disabled || busy}
      title={busy ? 'Saving.' : undefined}
      onChange={(e) => void run({ priority: e.target.value || null })}
      className={cn(
        'w-full cursor-pointer appearance-none rounded-md px-2 py-1 text-center text-xs font-medium',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-60',
        busy && 'animate-pulse',
        live ? PRIORITY_CLASS[live] : 'bg-muted text-muted-foreground',
      )}
    >
      <option value="" className="bg-background text-foreground">
        None
      </option>
      {PRIORITY_OPTIONS.map((p) => (
        <option key={p} value={p} className="bg-background text-foreground">
          {p}
        </option>
      ))}
    </select>
  )
}

/**
 * The authority level. Hassan Osman step four, db/migrations/0085.
 *
 * "Not set" is a real option and it is the first one, not a blank. A select whose
 * empty value renders as nothing invites the reading that the field does not
 * apply here; every task on this board needs an answer to it, and 359 of them do
 * not have one yet.
 *
 * The hint under each level is in the title attribute rather than only in the
 * panel, because the misunderstanding this field prevents happens while somebody
 * is choosing, not afterwards.
 */
export function AuthorityCell({
  value,
  save,
  disabled,
}: {
  value: TaskAuthority | null | undefined
  save: SavePatch
  disabled?: boolean
}) {
  const { busy, run, pending } = useSaver(save)
  const live = shown<TaskAuthority | null>(pending, 'authority_level', value ?? null)
  return (
    <select
      value={live ?? ''}
      disabled={disabled || busy}
      title={busy ? 'Saving.' : live ? AUTHORITY_HINT[live] : 'Nobody has set how much authority the owner has.'}
      onChange={(e) => void run({ authority_level: e.target.value || null })}
      className={cn(
        'w-full cursor-pointer appearance-none rounded-md px-2 py-1 text-center text-xs font-medium',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-60',
        busy && 'animate-pulse',
        live ? AUTHORITY_CLASS[live] : 'bg-muted text-muted-foreground',
      )}
    >
      <option value="" className="bg-background text-foreground">
        Not set
      </option>
      {AUTHORITY_OPTIONS.map((a) => (
        <option key={a} value={a} title={AUTHORITY_HINT[a]} className="bg-background text-foreground">
          {AUTHORITY_SHORT[a]}
        </option>
      ))}
    </select>
  )
}

// The assignee bubbles. An unlinked name keeps its bubble and gains a warning,
// rather than disappearing: "Deleted member" owns 18 of these tasks and
// Kgotlelelo Petje owns 33 without a login, and hiding either would make the
// board look tidier than the business is.
function Bubbles({ names, unlinked }: { names: string[]; unlinked: boolean }) {
  if (names.length === 0) {
    return (
      <span className="rounded-full border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground group-hover:border-foreground/40 group-hover:text-foreground">
        Unassigned
      </span>
    )
  }
  return (
    <span className="flex items-center -space-x-1.5">
      {names.slice(0, 4).map((n) => (
        <Tooltip key={n}>
          <TooltipTrigger asChild>
            <span
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-card',
                bubbleClass(n),
              )}
            >
              {initials(n)}
            </span>
          </TooltipTrigger>
          <TooltipContent>{n}</TooltipContent>
        </Tooltip>
      ))}
      {names.length > 4 && (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-card">
          +{names.length - 4}
        </span>
      )}
      {unlinked && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="ml-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            One of these names is not a person on record, so this task cannot be emailed to them.
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  )
}

/**
 * Owner, editable in place, one or many.
 *
 * The roster comes from public.v_task_people, which is deliberately wider than
 * the login list: it unions cockpit_access with every name already carrying
 * work. So a person with no login is offered, marked "no login", rather than
 * being quietly unassignable. A name that cannot be given work is how 33 tasks
 * ended up belonging to somebody who could not see them.
 *
 * With no roster or no save, this falls back to the read-only strip, which is
 * what the detail panel's subitem list wants.
 */
export function PeopleCell({
  names,
  unlinked,
  people,
  save,
  disabled,
  onClick,
}: {
  names: string[]
  unlinked: boolean
  people?: TaskPerson[]
  save?: SavePatch
  disabled?: boolean
  onClick?: () => void
}) {
  if (!people || !save || disabled) {
    return (
      <button type="button" onClick={onClick} className="group flex items-center">
        <Bubbles names={names} unlinked={unlinked} />
      </button>
    )
  }

  const choices: Choice[] = people.map((p) => ({
    value: p.name,
    label: p.name,
    hint: p.has_login ? undefined : 'no login',
    sub: p.open_tasks > 0 ? `${p.open_tasks} open, ${p.overdue_tasks} overdue` : 'nothing open',
  }))

  return (
    <MultiSelect
      choices={choices}
      selected={names}
      title="Who owns this"
      searchPlaceholder="Search people"
      onCommit={async (next) => {
        await save({ assignees: next })
      }}
    >
      <button
        type="button"
        className="group flex items-center rounded px-1 py-0.5 hover:bg-muted"
        aria-label="Change the owner"
      >
        <Bubbles names={names} unlinked={unlinked} />
      </button>
    </MultiSelect>
  )
}

/**
 * The task name, edited where it is read.
 *
 * A textarea rather than an input, because a third of the titles on this board
 * run past one line and an input hides everything after the caret. Enter saves,
 * Escape abandons, and blur saves: a person who clicks away has finished typing,
 * not changed their mind.
 *
 * `wrap` is what the header's wrap control turns on. Off, the name is cut at one
 * line and widening the column reveals more of it. On, it wraps onto up to three
 * lines, which shows the whole of almost every title on this board at the cost
 * of a taller row. Three lines and not unlimited: one 400 character title
 * pasted from an email should not push twenty rows off the screen.
 */
export function TitleCell({
  value,
  save,
  disabled,
  wrap,
  className,
}: {
  value: string
  save: SavePatch
  disabled?: boolean
  wrap?: boolean
  className?: string
}) {
  const { busy, run, pending } = useSaver(save)
  const live = shown<string>(pending, 'title', value)
  const [editing, setEditing] = useState(false)

  if (!editing || disabled) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
        title={busy ? 'Saving.' : disabled ? live : 'Click to rename'}
        className={cn(
          'group relative min-w-0 flex-1 rounded py-1.5 pl-1 pr-4 text-left hover:bg-muted',
          'disabled:hover:bg-transparent',
          wrap ? 'line-clamp-3 whitespace-pre-wrap break-words' : 'truncate',
          busy && 'animate-pulse',
          className,
        )}
      >
        {live}
        {!disabled && <EditPencil />}
      </button>
    )
  }

  return (
    <textarea
      autoFocus
      rows={wrap ? 3 : 2}
      defaultValue={value}
      disabled={busy}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={(e) => {
        setEditing(false)
        const next = e.target.value.trim()
        if (next && next !== value) void run({ title: next })
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          setEditing(false)
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          ;(e.target as HTMLTextAreaElement).blur()
        }
      }}
      className="min-w-0 flex-1 resize-none rounded border border-input bg-background px-1 py-1 text-sm"
    />
  )
}

/**
 * The linked vehicles, vehicle owners and drivers.
 *
 * One cell can carry more than one kind, because "the LY80CBGP claim" is about
 * a car AND the owner waiting for the money, and asking somebody to fill two
 * columns for one fact is how neither gets filled. Each chip says which kind it
 * is by its icon, and an offboarded car or an archived driver is struck through
 * rather than hidden: most of the oldest open work on this board is about a
 * vehicle or a person who has already left.
 */
export function LinkCell({
  kinds,
  links,
  options,
  save,
  disabled,
  title,
  onChipClick,
}: {
  // Which kinds this cell owns, in the order their sections appear.
  kinds: ('vehicle' | 'owner' | 'driver')[]
  // The links for each kind, keyed the same way.
  links: Record<string, TaskLink[]>
  options: TaskLinkOption[]
  save: (kind: 'vehicle' | 'owner' | 'driver', ids: string[]) => Promise<void>
  disabled?: boolean
  title: string
  onChipClick?: (kind: 'vehicle' | 'owner' | 'driver', id: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const shown = kinds.flatMap((k) => (links[k] ?? []).map((l) => ({ ...l, kind: k })))

  const SECTION: Record<string, string> = {
    vehicle: 'Vehicles',
    owner: 'Vehicle owners',
    driver: 'Drivers',
  }

  // Sorted by the cell's own kind order, not the order the endpoint happened to
  // return: the vehicle cell must open on Vehicles, and it was opening on
  // Vehicle owners because the query orders by kind alphabetically.
  const choices: Choice[] = options
    .filter((o) => kinds.includes(o.kind))
    .slice()
    .sort((a, b) => kinds.indexOf(a.kind) - kinds.indexOf(b.kind))
    .map((o) => ({
      value: `${o.kind}:${o.id}`,
      label: o.label,
      sub: o.sub_label,
      inactive: !o.is_active,
      hint: o.is_active ? undefined : (o.status ?? undefined),
      section: SECTION[o.kind],
    }))

  const selected = kinds.flatMap((k) => (links[k] ?? []).map((l) => `${k}:${l.id}`))

  const commit = async (next: string[]) => {
    setBusy(true)
    try {
      // One call per kind, and only for the kinds that actually changed, so
      // adding a driver does not rewrite the vehicle links and post an activity
      // row saying nothing happened.
      for (const kind of kinds) {
        const before = (links[kind] ?? []).map((l) => l.id).sort()
        const after = next
          .filter((v) => v.startsWith(`${kind}:`))
          .map((v) => v.slice(kind.length + 1))
          .sort()
        if (before.join('|') !== after.join('|')) await save(kind, after)
      }
    } finally {
      setBusy(false)
    }
  }

  const strip = (
    <span className="flex min-w-0 flex-wrap items-center gap-1">
      {shown.length === 0 ? (
        // Quiet until pointed at. A dashed "Link" pill on every one of 253 rows
        // is 253 invitations nobody asked for, and it drowns out the rows that
        // do carry a link.
        <span className="rounded-full px-1 py-0.5 text-[11px] text-muted-foreground/40 group-hover:text-muted-foreground">
          {disabled ? '—' : 'Link'}
        </span>
      ) : (
        shown.slice(0, 3).map((l) => (
          <Tooltip key={`${l.kind}:${l.id}`}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'inline-flex max-w-full items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px]',
                  l.kind === 'vehicle'
                    ? 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200'
                    : l.kind === 'owner'
                      ? 'bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-200'
                      : 'bg-teal-100 text-teal-900 dark:bg-teal-950 dark:text-teal-200',
                  !l.is_active && 'line-through opacity-70',
                )}
              >
                {l.kind === 'vehicle' ? (
                  <Car className="h-3 w-3 shrink-0" />
                ) : (
                  <UserRound className="h-3 w-3 shrink-0" />
                )}
                <span className="truncate">{l.label}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {SECTION[l.kind]}: {l.label}
              {l.sub_label ? ` · ${l.sub_label}` : ''}
              {l.is_active ? '' : ' · no longer active'}
            </TooltipContent>
          </Tooltip>
        ))
      )}
      {shown.length > 3 && (
        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
          +{shown.length - 3}
        </span>
      )}
      {busy && <span className="text-[10px] text-muted-foreground">saving…</span>}
    </span>
  )

  if (disabled) return strip

  return (
    <MultiSelect
      choices={choices}
      selected={selected}
      title={title}
      searchPlaceholder="Search by registration or name"
      onCommit={commit}
    >
      <button
        type="button"
        className="group flex w-full min-w-0 items-center rounded px-1 py-0.5 text-left hover:bg-muted"
        aria-label={title}
        onDoubleClick={
          onChipClick && shown.length > 0
            ? (e) => {
                e.preventDefault()
                onChipClick(shown[0].kind, shown[0].id)
              }
            : undefined
        }
      >
        {strip}
      </button>
    </MultiSelect>
  )
}

export function DateCell({
  value,
  field,
  save,
  disabled,
  tone,
  // What to draw when there is no date AND the absence itself means something.
  // Used for the 25 finished tasks imported from monday.com that carry no
  // completion date anywhere, where an empty cell reads as an oversight rather
  // than as a fact nobody has. Arrives with db/migrations/0232.
  placeholder,
}: {
  value: string | null
  field: string
  save: SavePatch
  disabled?: boolean
  tone?: string
  placeholder?: string
}) {
  const { busy, run, pending } = useSaver(save)
  const live = shown<string | null>(pending, field, value)
  const [editing, setEditing] = useState(false)

  // What the picker can hold, which is not what the API sent. See
  // dateInputValue in utils/tasks.ts: this used to be `value ?? ''`, the picker
  // silently refused the timestamp, and clicking away then wrote null over a
  // real date.
  const editValue = dateInputValue(live)

  if (editing && !disabled) {
    return (
      <input
        type="date"
        autoFocus
        defaultValue={editValue}
        disabled={busy}
        onBlur={(e) => {
          setEditing(false)
          const next = e.target.value || null
          // A picker we could not fill says nothing about what the person
          // wanted, so it must never be read as "clear this". Only a picker
          // that was showing the stored date can report that it was emptied.
          if (next === null && live !== null && editValue === '') return
          // Compared against the same shape the picker was given, not against
          // the raw value, or every blur looks like a change.
          if (next !== (editValue || null)) void run({ [field]: next })
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditing(false)
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        }}
        className="w-full rounded border border-input bg-background px-1 py-0.5 text-xs"
      />
    )
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setEditing(true)}
      title={busy ? 'Saving.' : disabled ? undefined : 'Click to change this date'}
      className={cn(
        'group relative w-full rounded px-1 py-0.5 text-left text-xs tabular-nums hover:bg-muted',
        'disabled:cursor-not-allowed disabled:hover:bg-transparent',
        busy && 'animate-pulse',
        live ? tone : 'text-muted-foreground/60',
      )}
    >
      {/* The underline goes under the value, not across the cell, so a column
          dragged to 300px does not draw a 300px line. */}
      <span
        className={cn(
          !disabled && 'underline decoration-dotted decoration-muted-foreground/40 underline-offset-2',
        )}
      >
        {live === null && placeholder ? (
          <span className="italic opacity-80">{placeholder}</span>
        ) : (
          shortDate(live)
        )}
      </span>
      {!disabled && <EditPencil />}
    </button>
  )
}

/**
 * A long text field, edited where it is read. The task's notes.
 *
 * ENTER DOES NOT SAVE HERE, and that is deliberately different from TitleCell.
 * A title is one line and Enter means "done". Notes are paragraphs and Enter
 * means a new line: a field where Enter closed the box would make it impossible
 * to write the second sentence. Blur saves, Escape abandons.
 *
 * Empty renders as an invitation rather than as nothing. A notes field that is
 * invisible until it already has content can never get any, which is part of
 * why 111 of the 253 imported tasks carry no context at all.
 */
export function NotesCell({
  value,
  field,
  save,
  disabled,
  placeholder,
}: {
  value: string | null
  field: string
  save: SavePatch
  disabled?: boolean
  placeholder: string
}) {
  const { busy, run, pending } = useSaver(save)
  const live = shown<string | null>(pending, field, value)
  const [editing, setEditing] = useState(false)

  if (editing && !disabled) {
    return (
      <textarea
        autoFocus
        rows={5}
        defaultValue={value ?? ''}
        disabled={busy}
        onBlur={(e) => {
          setEditing(false)
          const next = e.target.value.trim() || null
          if (next !== (value ?? null)) void run({ [field]: next })
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            setEditing(false)
          }
        }}
        className="w-full resize-y rounded border border-input bg-background px-2 py-1.5 text-sm"
      />
    )
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setEditing(true)}
      title={busy ? 'Saving.' : disabled ? undefined : 'Click to edit'}
      className={cn(
        'group relative w-full whitespace-pre-wrap break-words rounded py-1 pl-1 pr-5 text-left text-sm',
        'hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent',
        busy && 'animate-pulse',
        !live && 'text-muted-foreground/60',
      )}
    >
      {live ?? placeholder}
      {!disabled && <Pencil className={cn(EDIT_HINT, 'right-1 top-2 h-3.5 w-3.5 translate-y-0')} />}
    </button>
  )
}

/**
 * A system timestamp, read only.
 *
 * Deliberately NOT a DateCell. DateCell is an editable picker, and created_at
 * and updated_at are records of what happened rather than intentions somebody
 * can revise: a created date you can type is not a created date. Making them
 * editable would also hand somebody the ability to rewrite the audit trail from
 * the board by accident.
 *
 * The cell shows the day and carries the full local timestamp in its title,
 * because "26 Aug" answers "is this stale" while the hover answers "was that
 * before or after the meeting".
 */
export function StampCell({ value }: { value: string | null }) {
  if (!value) {
    // A missing stamp shows as missing, never as a guess or a dash that reads
    // like a real value. See CLAUDE.md rule 6.
    return <span className="text-xs text-muted-foreground/50">not recorded</span>
  }
  return (
    <span className="truncate text-xs tabular-nums text-muted-foreground" title={longDateTime(value)}>
      {shortDate(value)}
    </span>
  )
}

export function Cell({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('min-w-0 px-2 py-1', className)}>{children}</div>
}
